import { API_URL as API } from '@/lib/backendUrl';
import { resetPromoBarStatus } from '@/components/layout/PromoStickyBar';

// Данные гостевой формы расчёта карты на главной. Тот же ключ читает
// LazyChart: гость считает карту до регистрации, и его дата остаётся здесь.
export const NATAL_FORM_STORAGE_KEY = 'astrofeel_natal_form';

// Первый расчёт вошедшего заводит профиль рождения, если его ещё нет.
// Не блокирует расчёт: карта не должна зависеть от этого запроса.
//
// Один запрос, а не два. «Есть ли уже профиль» решает сервер по флагу
// only_if_absent: он проверяет это в той же транзакции и на существующий
// профиль отвечает status=skipped, ничего не переписывая.
//
// Ответ пишем в консоль: это единственное место, где владелец может увидеть,
// дошёл ли запрос и что ответил сервер.
const SEED_LOG = '[natal-profile]';

// Имя из гостевой формы, если человек его указал. Лежит внутри formData
// рядом с датой: форма сохраняется в localStorage целиком.
export function readNatalFormName(saved) {
  const src = saved || loadNatalFormFromStorage();
  const name = (src && src.formData && src.formData.name) || (src && src.name) || '';
  return String(name).trim();
}

export function loadNatalFormFromStorage() {
  try {
    const s = localStorage.getItem(NATAL_FORM_STORAGE_KEY);
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
}

// Засеваем только заведомо полные данные: без города и координат карта
// всё равно не считается, а битый localStorage не должен ронять регистрацию.
export function isNatalFormComplete(formData) {
  if (!formData) return false;
  const day = parseInt(formData.day, 10), month = parseInt(formData.month, 10), year = parseInt(formData.year, 10);
  const hour = parseInt(formData.hour, 10), minute = parseInt(formData.minute, 10);
  const lat = parseFloat(formData.latitude), lon = parseFloat(formData.longitude);
  // Отмечено «не знаю время» — данные полные и без часов: карта по ним
  // строится, а профиль запомнит и сам флаг.
  const timeOk = !!formData.timeUnknown
    || (!isNaN(hour) && hour >= 0 && hour <= 23 && !isNaN(minute) && minute >= 0 && minute <= 59);
  return day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900 && year <= 2100
    && timeOk
    && !isNaN(lat) && !isNaN(lon)
    && !!(formData.city || '').trim();
}

export async function seedNatalProfile({ formData, citySearch }) {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    console.warn(`${SEED_LOG} пропуск: нет auth_token`);
    return false;
  }

  const pad = (v) => String(parseInt(v, 10)).padStart(2, '0');
  // Имя засеваем вместе с датой: дальше по нему здоровается кабинет
  // и первая строка каждого письма. Пустое не шлём.
  const firstName = String((formData && formData.name) || '').trim();
  // «Не знаю время рождения» из гостевой формы едет в профиль вместе с
  // датой: иначе после регистрации профиль получил бы пустое время без
  // объяснения, и карта в кабинете снова попросила бы часы.
  const timeUnknown = !!(formData && formData.timeUnknown);
  const payload = {
    ...(firstName ? { first_name: firstName } : {}),
    birth_date: `${formData.year}-${pad(formData.month)}-${pad(formData.day)}`,
    birth_time: timeUnknown ? '' : `${pad(formData.hour)}:${pad(formData.minute)}`,
    birth_time_unknown: timeUnknown,
    birth_place_text: citySearch || formData.city || '',
    lat: parseFloat(formData.latitude),
    lon: parseFloat(formData.longitude),
    timezone: formData.timezone || undefined,
  };

  const url = `${API}/natal/profile?only_if_absent=true`;
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const body = await res.text().catch(() => '');
    if (!res.ok) {
      console.warn(`${SEED_LOG} POST ${url} → ${res.status}`, body, 'отправляли:', payload);
      return false;
    }
    console.info(`${SEED_LOG} POST ${url} → ${res.status}`, body);
    // Верхняя плашка держит «есть ли дата рождения» в кэше на сессию и сама
    // о засеве не узнает — сбрасываем, чтобы она перечитала статус.
    resetPromoBarStatus();
    return true;
  } catch (e) {
    console.warn(`${SEED_LOG} POST ${url} не ушёл:`, e && e.message, 'отправляли:', payload);
    return false;
  }
}

/**
 * После регистрации из окна программы: данные рождения её формы — в кабинет,
 * если он пуст (only_if_absent). Формы программ пишут поля по-разному:
 * day/month/year, hour/minute или time «ЧЧ:ММ», timeUnknown / time_unknown,
 * city, latitude/longitude (или lat/lon), timezone. Неполные данные
 * (без места или времени и без «не знаю») не засеваются. → true, если ушло.
 */
export function birthFormForSeed(f) {
  if (!f || typeof f !== 'object') return null;
  const [th, tm] = String(f.time || '').split(':');
  const hour = f.hour !== undefined && f.hour !== '' ? f.hour : (th || '');
  const minute = f.minute !== undefined && f.minute !== '' ? f.minute : (tm || '');
  const timeUnknown = !!(f.timeUnknown || f.time_unknown);
  return {
    day: f.day, month: f.month, year: f.year, hour, minute, timeUnknown,
    city: f.city || f.location_name || '',
    latitude: f.latitude ?? f.lat, longitude: f.longitude ?? f.lon,
    timezone: f.timezone || '',
    ...(f.name ? { name: f.name } : {}),
  };
}

export async function seedBirthAfterSignup(form) {
  const data = birthFormForSeed(form);
  if (!data || !isNatalFormComplete(data)) return false;
  return seedNatalProfile({ formData: data, citySearch: data.city });
}
